/* eslint-disable max-len */
/* eslint-disable multiline-ternary */
/* eslint-disable no-console */
/* eslint-disable import/extensions */
import {
	Box,
	Button,
	Grid,
	InputAdornment,
	TextField,
	Typography,
	CircularProgress
} from '@mui/material';
import React, { useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import MainCard from '../../../../../../../../../../../UiComponents/Cards/MainCard';
import TreeView from '../../../../TreeView';
import { IoSearch } from 'react-icons/io5';
import { LanguageContext } from '../../../../../../../../../../../libs/Context';
import NoData from '../../../../../../../../../../../components/Common/NoData';
import CreateContentNodataImg from '../../../../../../../../../../../assets/Resources/CreateContentNodataImg.svg';
import { createCategory, getCategories } from '../../../../../../../../../../../containers/actions/resource_configuration';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useResourceHelper from '../../../../ContentCards/useResourceHelper';

interface CategorySliderProps {
	artcileCreate?: string;
	categories?: string[];
	onChange?: (categories: string[]) => void;
}

const CategorySlider: React.FC<CategorySliderProps> = (props) =>
{
	const { language } = useContext(LanguageContext);
	const { artcileCreate, categories = [], onChange } = props;
	const [ noData, setNoData ] = useState(false);
	const [ loading, setLoading ] = useState(true);
	const [ categoryName, setCategoryName ] = useState('');
	const [ searchQuery, setSearchQuery ] = useState('');
	const [ selectedCategory, setSelectedCategory ] = useState<string>(categories[0] || '');
	const [ charCount, setCharCount ] = useState(0);
	const [ isCreating, setIsCreating ] = useState(false);
	const [ refreshCounter, setRefreshCounter ] = useState(0);

	// Use refs to track previous values
	const prevCategoriesRef = useRef<string[]>([]);
	const prevSelectedCategoryRef = useRef<string>('');
	const isMountedRef = useRef(false);

	// Get company ID from Redux state
	const companyId = useSelector((state: any) => state.mysession.session?.companyId);
	const { isAdminView } = useResourceHelper();
	// Use a stable key for the TreeView component
	const treeViewKey = useMemo(() => 'category-tree-view', []);

	// Compare arrays helper for single selection
	const areArraysEqual = (arr1: string[], arr2: string[]) =>
	{
		if (arr1.length !== arr2.length) return false;
		const sorted1 = [ ...arr1 ].sort();
		const sorted2 = [ ...arr2 ].sort();

		return sorted1.every((value, index) => value === sorted2[index]);
	};

	// Check if categories have actually changed
	useEffect(() =>
	{
		if (!areArraysEqual(prevCategoriesRef.current, categories))
		{
			prevCategoriesRef.current = categories;
			setSelectedCategory(categories[0] || '');
		}
	}, [ categories ]);

	// Initial categories check
	useEffect(() =>
	{
		const checkCategories = async () =>
		{
			const from = isAdminView ? 'Configuration' : 'MyResource';
			// Skip if already mounted or no companyId

			if (isMountedRef.current || !companyId)
			{
				return;
			}

			try
			{
				setLoading(true);
				const response = await getCategories(companyId, { parentCategoryId: 'null', level: 0, from });

				setNoData(response.categories.length === 0);
				isMountedRef.current = true;
			}
			catch (error)
			{
				console.error('Error checking categories:', error);
				setNoData(true);
			}
			finally
			{
				setLoading(false);
			}
		};

		checkCategories();
	}, [ companyId ]);

	// Handle category name input change
	const handleCategoryNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
	{
		const value = e.target.value;

		setCategoryName(value);
		setCharCount(value.length);
	}, []);

	// Handle search query change
	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
	{
		setSearchQuery(e.target.value);
	}, []);

	// Handle category selection from TreeView
	const handleCategorySelection = useCallback((selectedIds: string | string[]) =>
	{
		// For radio selection, always get the single ID
		const categoryId = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;

		// Only update if selection actually changed
		if (prevSelectedCategoryRef.current !== categoryId)
		{
			prevSelectedCategoryRef.current = categoryId;
			setSelectedCategory(categoryId);

			// Notify parent of changes with array format
			if (onChange)
			{
				onChange(categoryId ? [ categoryId ] : []);
			}
		}
	}, [ onChange ]);

	// Code for the TreeView section
	const renderTreeView = useCallback(() =>
	{
		return (
			<TreeView
				key={treeViewKey}
				selectionType='radio'
				articleCreate={artcileCreate}
				onSelect={handleCategorySelection}
				initialSelectedId={selectedCategory}
				refreshTrigger={refreshCounter}
				searchTerm={searchQuery}
			/>
		);
	}, [ treeViewKey, artcileCreate, handleCategorySelection, selectedCategory, refreshCounter, searchQuery ]);

	// Handle adding a new category
	const handleAddCategory = useCallback(async () =>
	{
		// Validate that we have both a category name and company ID
		if (!categoryName.trim() || !companyId)
		{
			toast.error('Category name or company ID is missing');

			return;
		}

		try
		{
			setIsCreating(true);

			// Call the createCategory API
			const response = await createCategory({
				name        : categoryName.trim(),
				companyId,
				description : '',
				visibility  : 'public'
			});

			// If successful, replace existing category with new one (single selection)
			if (response && response._id)
			{
				const newCategoryId = response._id;

				// Update refs and state
				prevSelectedCategoryRef.current = newCategoryId;
				setSelectedCategory(newCategoryId);

				// Notify parent component with array format
				if (onChange)
				{
					onChange([ newCategoryId ]);
				}

				// Clear the input field and show the TreeView
				setCategoryName('');
				setCharCount(0);
				setNoData(false);

				// Increment the refresh counter to trigger TreeView reload
				setRefreshCounter((prev) => prev + 1);

				toast.success('Category created successfully');
			}
		}
		catch (error)
		{
			console.error('Error creating category:', error);
			toast.error('Failed to create category. Please try again.');
		}
		finally
		{
			setIsCreating(false);
		}
	}, [ categoryName, companyId, onChange ]);

	// Render loading state
	if (loading)
	{
		return (
			<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
				<CircularProgress />
			</Box>
		);
	}

	// Render no data state
	if (noData)
	{
		return (
			<NoData
				image={CreateContentNodataImg}
				title={language?.noCategories || 'No Categories Found'}
				description={language?.createCategories || 'Create categories to organize your content'}
			/>
		);
	}

	return (
		<Grid container spacing={2}>
			{/* Category Creation Section */}
			<Grid item xs={12}>
				<MainCard>
					<Box p={2}>
						<Typography variant='subtitle1' gutterBottom>
							{language?.createNewCategory || 'Create New Category'}
						</Typography>
						<TextField
							fullWidth
							value={categoryName}
							onChange={handleCategoryNameChange}
							placeholder={language?.enterCategoryName || 'Enter category name'}
							disabled={isCreating}
							InputProps={{
								endAdornment : (
									<InputAdornment position='end'>
										<Typography variant='caption' color='textSecondary'>
											{charCount}/50
										</Typography>
									</InputAdornment>
								)
							}}
						/>
						<Box mt={2} display='flex' justifyContent='flex-end'>
							<Button
								variant='contained'
								onClick={handleAddCategory}
								disabled={!categoryName.trim() || isCreating}
							>
								{isCreating ? (
									<CircularProgress size={24} />
								) : (
									language?.addCategory || 'Add Category'
								)}
							</Button>
						</Box>
					</Box>
				</MainCard>
			</Grid>

			{/* Search and TreeView Section */}
			<Grid item xs={12}>
				<MainCard>
					<Box p={2}>
						<TextField
							fullWidth
							value={searchQuery}
							onChange={handleSearchChange}
							placeholder={language?.searchCategories || 'Search categories'}
							InputProps={{
								startAdornment : (
									<InputAdornment position='start'>
										<IoSearch />
									</InputAdornment>
								)
							}}
							sx={{ mb: 2 }}
						/>
						{renderTreeView()}
					</Box>
				</MainCard>
			</Grid>
		</Grid>
	);
};

export default React.memo(CategorySlider);
